// Animation route — canvas frame renderer.
//
// drawAnimationFrame(ctx, spec, tMs, w, h) draws the state of the
// animation at time t: the MOTION layer (image sequence) first, then
// per-slide CAPTIONS, then the optional TITLE overlay on top. PURE with
// respect to time: same (spec, t) in → same pixels out, which is what
// makes the preview loop, the scrub bar, and the MP4 export
// pixel-identical (they all just pick a t).
//
// Text timing model (Tier 1, Jun 11):
//   - TITLE (spec.text) spans the whole video — entrances complete by
//     ~60% of total, then hold. Centered, radial legibility scrim.
//   - CAPTIONS (spec.captions[i]) are slide-attached — each enters with
//     its slide, animates via spec.captionStyle, and fades out as the
//     slide transitions away. Lower-third, bottom gradient scrim.
//
// All sizes derive from the canvas dimensions so 9:16 and 1:1 share the
// same code paths. Fonts use the self-hosted Manrope (callers must
// `await document.fonts.ready` before export so measureText doesn't run
// against a fallback font — same rule as composeRender).

import type { AnimationSpec, TextStyle } from './types';
import { specDurationMs } from './types';

// ---------------------------------------------------------------------------
// Easing
// ---------------------------------------------------------------------------

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

function easeInOutCubic(x: number): number {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

function easeOutCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
}

/** Springy overshoot — used by word-pop. */
function easeOutBack(x: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
}

// ---------------------------------------------------------------------------
// Cover-fit image draw (with optional zoom/pan)
// ---------------------------------------------------------------------------

/**
 * Draw `img` covering the full canvas (center-crop), optionally scaled
 * by `zoom` (≥1) around the center, with a pan offset expressed as a
 * fraction of the OVERFLOW the zoom created (so pan never reveals
 * blank edges).
 */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  w: number,
  h: number,
  zoom = 1,
  panX = 0,
  panY = 0,
): void {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih) return;
  const cover = Math.max(w / iw, h / ih) * zoom;
  const dw = iw * cover;
  const dh = ih * cover;
  const maxPanX = (dw - w) / 2;
  const maxPanY = (dh - h) / 2;
  const dx = (w - dw) / 2 + panX * maxPanX;
  const dy = (h - dh) / 2 + panY * maxPanY;
  ctx.drawImage(img, dx, dy, dw, dh);
}

// ---------------------------------------------------------------------------
// Motion layer
// ---------------------------------------------------------------------------

/** Slide arithmetic shared by the motion layer + captions. */
function slideAt(spec: AnimationSpec, tMs: number) {
  const n = spec.images.length;
  const per = spec.perImageMs;
  const trans = Math.min(spec.transitionMs, per * 0.6);
  const total = n * per;
  const t = ((tMs % total) + total) % total; // loop-safe
  const idx = Math.min(Math.floor(t / per), n - 1);
  const local = t - idx * per;
  const next = (idx + 1) % n;
  const tp = local > per - trans ? clamp01((local - (per - trans)) / trans) : 0;
  return { n, per, trans, total, t, idx, local, next, tp };
}

function drawMotion(
  ctx: CanvasRenderingContext2D,
  spec: AnimationSpec,
  tMs: number,
  w: number,
  h: number,
): void {
  if (spec.images.length === 0) return;
  const { n, per, idx, local, next, tp } = slideAt(spec, tMs);

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);

  switch (spec.motionStyle) {
    case 'crossfade': {
      ctx.globalAlpha = 1;
      drawCover(ctx, spec.images[idx], w, h);
      if (tp > 0 && n > 1) {
        ctx.globalAlpha = easeInOutCubic(tp);
        drawCover(ctx, spec.images[next], w, h);
        ctx.globalAlpha = 1;
      }
      break;
    }

    case 'slide': {
      const shift = n > 1 ? easeInOutCubic(tp) * w : 0;
      ctx.save();
      ctx.translate(-shift, 0);
      drawCover(ctx, spec.images[idx], w, h);
      ctx.restore();
      if (shift > 0) {
        ctx.save();
        ctx.translate(w - shift, 0);
        drawCover(ctx, spec.images[next], w, h);
        ctx.restore();
      }
      break;
    }

    case 'kenburns': {
      // Continuous zoom 1.00→1.10 over the slide's lifetime, alternating
      // pan direction per image. Crossfade between slides.
      const prog = clamp01(local / per);
      const dir = idx % 2 === 0 ? 1 : -1;
      ctx.globalAlpha = 1;
      drawCover(ctx, spec.images[idx], w, h, 1 + 0.1 * prog, 0.6 * dir * prog, 0.3 * dir * prog);
      if (tp > 0 && n > 1) {
        ctx.globalAlpha = easeInOutCubic(tp);
        drawCover(ctx, spec.images[next], w, h, 1, 0, 0);
        ctx.globalAlpha = 1;
      }
      break;
    }

    case 'zoom-punch': {
      // Hard cut; every slide OPENS with a fast punch (1.18→1.0 over
      // ~280ms, ease-out) then keeps a subtle drift to 1.04. Reads as a
      // beat-synced cut even without audio.
      const punch = clamp01(local / 280);
      const drift = clamp01(local / per);
      const zoom = punch < 1
        ? 1.18 - 0.18 * easeOutCubic(punch)
        : 1 + 0.04 * drift;
      drawCover(ctx, spec.images[idx], w, h, zoom);
      break;
    }

    case 'wipe': {
      // Next image revealed by a vertical edge sweeping left→right.
      drawCover(ctx, spec.images[idx], w, h);
      if (tp > 0 && n > 1) {
        const edge = easeInOutCubic(tp) * w;
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, edge, h);
        ctx.clip();
        drawCover(ctx, spec.images[next], w, h);
        ctx.restore();
        // Thin highlight line on the wipe edge sells the sweep.
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillRect(edge - 3, 0, 6, h);
      }
      break;
    }

    case 'flash-cut': {
      // Hard cut through a white flash: alpha ramps up over the last
      // 120ms of a slide and back down over the first 120ms of the next.
      const FLASH = 120;
      drawCover(ctx, spec.images[idx], w, h);
      let flash = 0;
      if (n > 1) {
        if (local > per - FLASH) flash = (local - (per - FLASH)) / FLASH;
        else if (local < FLASH) flash = 1 - local / FLASH;
      }
      if (flash > 0) {
        ctx.fillStyle = `rgba(255,255,255,${(0.9 * flash).toFixed(3)})`;
        ctx.fillRect(0, 0, w, h);
      }
      break;
    }

    case 'mosaic-scroll': {
      // Bg shown between the gapped tiles. Overrides the global black fill
      // above (only mosaic-scroll + card-swipe expose the bg; the other 6
      // styles cover the canvas edge-to-edge).
      ctx.fillStyle = spec.backgroundColor || '#EDEDED';
      ctx.fillRect(0, 0, w, h);
      drawMosaicScroll(ctx, spec, tMs, w, h);
      break;
    }

    case 'card-swipe': {
      // Bg shown around the floating hero card + side peeks.
      ctx.fillStyle = spec.backgroundColor || '#EDEDED';
      ctx.fillRect(0, 0, w, h);
      drawCardSwipe(ctx, spec, tMs, w, h);
      break;
    }

    case 'marquee-rows': {
      // Bg shown in the GAPs between rows (and at left/right edges if tiles
      // don't fill row width). Defaults to dark — matches the cinematic
      // black bands in the reference. Light bg also works for editorial.
      ctx.fillStyle = spec.backgroundColor || '#1A1A1A';
      ctx.fillRect(0, 0, w, h);
      drawMarqueeRows(ctx, spec, tMs, w, h);
      break;
    }

    case 'stop-motion-grid': {
      // Bg shows BOTH between tiles AND through "empty" tiles (cells that
      // happen to be in their hide state this tick). Defaults to a warm
      // cream — matches the mood-board reference's neutral surface.
      ctx.fillStyle = spec.backgroundColor || '#F5F1EA';
      ctx.fillRect(0, 0, w, h);
      drawStopMotionGrid(ctx, spec, tMs, w, h);
      break;
    }

    case 'angled-cut': {
      // Bg only shows during slide phases where image hasn't filled the
      // canvas yet. At rest both regions are aligned → full image covers
      // the canvas, bg invisible. Default to dark — gives the slide-in
      // moments more drama.
      ctx.fillStyle = spec.backgroundColor || '#1A1A1A';
      ctx.fillRect(0, 0, w, h);
      drawAngledCut(ctx, spec, tMs, w, h);
      break;
    }

    case 'collage-stack': {
      // Bg shows around the stacked cards (each card is smaller than canvas).
      // Default cream — matches the paper/scrapbook collage feel.
      ctx.fillStyle = spec.backgroundColor || '#F5F1EA';
      ctx.fillRect(0, 0, w, h);
      drawCollageStack(ctx, spec, tMs, w, h);
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// Mosaic Scroll — 3-column grid that scrolls upward with ease + pauses.
//
// Per-frame: each column has its own scroll position scrollY(t) that follows
// a piecewise cycle of [ease for scrollMs → hold for holdMs] repeating. The
// columnStaggerMs offset means adjacent columns are at different phases at
// any moment — that asymmetry is what makes the grid feel alive vs. a
// single video wall. Tiles in each column are laid out vertically at native
// aspect (column width × img.naturalH / img.naturalW) and the source image
// list is repeated as many times as needed so the column's content is
// always ≥ canvas height; scrollY is then taken modulo the base content
// height for seamless looping. Tiles outside the canvas are skipped per
// frame so render cost stays bounded regardless of repeat factor.
// ---------------------------------------------------------------------------

function drawMosaicScroll(
  ctx: CanvasRenderingContext2D,
  spec: AnimationSpec,
  tMs: number,
  w: number,
  h: number,
): void {
  const cfg = spec.motionConfig?.kind === 'mosaic-scroll' ? spec.motionConfig : null;
  // Locked to 3 for center-focus layout (Jun 13 decision: the middle column
  // is the hero, side columns bleed off the canvas edge so only their inner
  // halves peek through — focuses attention on one image at a time). The
  // `columns` config field is accepted for forward-compat but ignored.
  const COLUMNS = 3;
  void cfg?.columns;
  const SCROLL_MS = Math.max(200, cfg?.scrollMs ?? 1200);
  const HOLD_MS = Math.max(0, cfg?.holdMs ?? 800);
  const STAGGER_MS = cfg?.columnStaggerMs ?? 200;
  // GAP proportional to canvas height (Jun 15 v4 — was fixed 80px).
  // Hard-coding broke the loop seam math at preview size (half-resolution
  // canvas) because GAP scaled differently than colW/SCROLL_PER_CYCLE,
  // making baseContent/SCROLL_PER_CYCLE ratio canvas-size-dependent.
  // h × 0.06 ≈ 81 at the 4:5 default export size (1350h) → matches the
  // prior visual; ≈ 40 at half-size preview (675h) → math holds. Applies
  // to BOTH horizontal gap between columns AND vertical gap between tiles.
  const GAP = h * 0.06;
  const RADIUS = Math.round(w * 0.018); // ~19px @ 1080w
  const CYCLE_MS = SCROLL_MS + HOLD_MS;
  // Each scroll step moves the column by ~45% of the canvas height. Fixed
  // amount (not "one row") so the rhythm is consistent regardless of tile
  // height variation across columns.
  const SCROLL_PER_CYCLE = h * 0.45;
  // Shift the global clock so the first visible frame is already mid-scroll
  // (not in hold) — the page-load impression should be motion, not stillness.
  const GLOBAL_OFFSET = SCROLL_MS * 0.4;

  const total = spec.images.length;
  if (total === 0) return;

  // Distribute images across columns (Jun 15 v4 — center-priority).
  //
  // Center-focus geometry means only col 1 (CENTER) is fully visible at
  // 60% canvas width; cols 0 (LEFT) and 2 (RIGHT) sit mostly off-canvas
  // and only their inner edges peek through. The original distribution
  // (image_k → col_k%3) only put every 3rd image in the visible center
  // column — user reported "I selected 4, can't see 3 of them". Fix:
  // ALL images flow through the CENTER column in user's natural order.
  // Side columns get the SAME image set rotated by N/3 so they show
  // DIFFERENT images at any moment than the center → the peek-through
  // edges remain visually distinct rather than mirroring the hero.
  //
  // For 1-2 images, rotation collapses to no-op (all cols identical
  // sets) — fine since with so few images there's not much variety to
  // distribute anyway. STAGGER_MS phase offsets still provide subtle
  // motion variety in that case.
  const colImages: HTMLImageElement[][] = Array.from({ length: COLUMNS }, () => []);
  const sideRotation = Math.max(1, Math.floor(total / COLUMNS));
  for (let col = 0; col < COLUMNS; col++) {
    // col 1 (center) gets natural order. Col 0 rotates +sideRotation,
    // col 2 rotates +2×sideRotation — at any moment these three columns
    // show three different images from the user's set.
    const rotation = col === 1 ? 0 : col * sideRotation;
    for (let i = 0; i < total; i++) {
      colImages[col].push(spec.images[(rotation + i) % total]);
    }
  }

  // Center-focus geometry: each column is 60% of the canvas wide (so the
  // center column = 60% of the canvas — the hero). The center column is
  // centered horizontally; the two side columns are offset by (colW + GAP)
  // on either side, putting most of their rect off-canvas (only thin slivers
  // peek through). Matches the Pinterest reference's "1 image at a time"
  // feel, with the bumps from Jun 13 — tile +20%, GAP +100% — to lean
  // further into the hero focus.
  const colW = w * 0.6;
  const centerX = (w - colW) / 2;
  const sideShift = colW + GAP;
  const colXs = [centerX - sideShift, centerX, centerX + sideShift];

  for (let col = 0; col < COLUMNS; col++) {
    const imgs = colImages[col];
    if (imgs.length === 0) continue;
    const colX = colXs[col];

    // Native-aspect tile heights for this column's base set.
    const tileHeightOf = (img: HTMLImageElement): number => {
      const ratio = img.naturalHeight / Math.max(1, img.naturalWidth);
      return colW * (Number.isFinite(ratio) && ratio > 0 ? ratio : 1);
    };

    // Compute baseContent for ONE pass through `imgs` first.
    let passContent = 0;
    for (const img of imgs) {
      passContent += tileHeightOf(img) + GAP;
    }
    if (passContent <= 0) continue;

    // Ensure baseContent ≥ canvas height (Jun 15 v3 — was: when 1 image
    // with non-portrait aspect ratio is in the column, baseContent =
    // tileH + GAP can be shorter than canvas height. The wrap loop below
    // only draws rep=-1, 0, 1 (3 copies) — when 2 × baseContent <
    // canvas_height the +1 copy doesn't reach the bottom → visible empty
    // space at the bottom of the canvas. Replicate the image list as
    // many times as needed so baseContent always fills the viewport).
    //
    // Cap at 30 reps as a safety to avoid pathological loops with
    // degenerate inputs (e.g. naturalWidth=0).
    const repeatCount = Math.min(30, Math.max(1, Math.ceil(h / passContent)));
    const finalImgs: HTMLImageElement[] = [];
    for (let r = 0; r < repeatCount; r++) {
      finalImgs.push(...imgs);
    }

    const baseHeights = finalImgs.map(tileHeightOf);
    const baseYs: number[] = [];
    let yAcc = 0;
    for (const th of baseHeights) {
      baseYs.push(yAcc);
      yAcc += th + GAP;
    }
    const baseContent = yAcc; // total height of one repeat (incl. trailing gap)
    if (baseContent <= 0) continue;

    // Phase-shifted local time for this column. Modulo into [0, CYCLE_MS) +
    // the cycle index, both signed-correct so column 1+ starting "ahead" of
    // column 0 (negative localTime) wraps cleanly.
    const localTime = tMs + GLOBAL_OFFSET - col * STAGGER_MS;
    const cycleIdx = Math.floor(localTime / CYCLE_MS);
    const localInCycle = ((localTime % CYCLE_MS) + CYCLE_MS) % CYCLE_MS;
    let scrollY: number;
    if (localInCycle < SCROLL_MS) {
      const p = easeInOutCubic(localInCycle / SCROLL_MS);
      scrollY = (cycleIdx + p) * SCROLL_PER_CYCLE;
    } else {
      // Hold phase — pinned at the end of this cycle's scroll.
      scrollY = (cycleIdx + 1) * SCROLL_PER_CYCLE;
    }
    // Wrap so we draw within one base content height. JS modulo is sign-
    // preserving for negatives — normalise back into [0, baseContent).
    scrollY = ((scrollY % baseContent) + baseContent) % baseContent;

    // Draw all visible tiles. Repeat factor of {-1, 0, 1} ensures the
    // wrap is covered without leaving a gap at the join. `finalImgs` was
    // expanded above so baseContent ≥ canvas height — guarantees the
    // three reps span the viewport even with 1 image of square aspect.
    for (let rep = -1; rep <= 1; rep++) {
      for (let i = 0; i < finalImgs.length; i++) {
        const tileY = baseYs[i] + rep * baseContent - scrollY;
        const tileH = baseHeights[i];
        if (tileY + tileH < -1 || tileY > h + 1) continue;
        drawRoundedImageCover(ctx, finalImgs[i], colX, tileY, colW, tileH, RADIUS);
      }
    }
  }
}

/** Draw `img` cover-fit into the rounded rectangle (x,y,w,h). Inline rather
 *  than reusing drawCover so we don't apply zoom/pan + can clip to the rounded
 *  rect cheaply. */
function drawRoundedImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
): void {
  ctx.save();
  ctx.beginPath();
  const r = Math.min(radius, w / 2, h / 2);
  // roundRect is in modern Chrome/Safari/Firefox; fall back to a plain rect
  // when missing (very old browser → no rounding, still draws correctly).
  if (typeof (ctx as CanvasRenderingContext2D & { roundRect?: unknown }).roundRect === 'function') {
    (ctx as CanvasRenderingContext2D & { roundRect: (x: number, y: number, w: number, h: number, r: number) => void })
      .roundRect(x, y, w, h, r);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.clip();
  // Cover-fit inside the tile rect.
  const ir = img.naturalHeight / Math.max(1, img.naturalWidth);
  const tr = h / w;
  let drawW = w, drawH = h, dx = x, dy = y;
  if (ir > tr) {
    // Image taller than tile → match width, overflow vertically (clipped).
    drawW = w;
    drawH = w * ir;
    dx = x;
    dy = y + (h - drawH) / 2;
  } else {
    drawH = h;
    drawW = h / ir;
    dx = x + (w - drawW) / 2;
    dy = y;
  }
  ctx.drawImage(img, dx, dy, drawW, drawH);
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Card Swipe — single hero "card" sweeps right-to-left, with the previous +
// next cards peeking at the canvas edges. Each card is a white rounded-rect
// with a subtle drop shadow (the chrome) holding the user's image inside.
//
// Per-frame motion mirrors mosaic-scroll's piecewise math but horizontal:
// each cycle is [ease over transitionMs → hold over holdMs], and on each
// cycle the carousel shifts left by exactly one stride (CARD_W + GAP). The
// modulo-wrap covers the seam by drawing repeats at offsets {-1, 0, +1} of
// the total content width.
// ---------------------------------------------------------------------------

function drawCardSwipe(
  ctx: CanvasRenderingContext2D,
  spec: AnimationSpec,
  tMs: number,
  w: number,
  h: number,
): void {
  const cfg = spec.motionConfig?.kind === 'card-swipe' ? spec.motionConfig : null;
  const TRANSITION_MS = Math.max(200, cfg?.transitionMs ?? 800);
  const HOLD_MS = Math.max(0, cfg?.holdMs ?? 1500);
  const CYCLE_MS = TRANSITION_MS + HOLD_MS;
  // MAX bounds (was fixed card dimensions; now upper-limit per Jun 15 v3
  // aspect-aware change). Each card scales DOWN from these to match its
  // own image's natural aspect — portrait images get a narrower card,
  // landscape images get a shorter one. The rhythm of the swipe stays
  // constant because STRIDE uses MAX_CARD_W, NOT each card's own width.
  const MAX_CARD_W = w * (cfg?.cardWidthFrac ?? 0.55);
  const MAX_CARD_H = h * (cfg?.cardHeightFrac ?? 0.75);
  const MAX_CARD_ASPECT = MAX_CARD_W / MAX_CARD_H;
  const GAP = 80;                       // matches mosaic-scroll's spacing
  const STRIDE = MAX_CARD_W + GAP;

  const total = spec.images.length;
  if (total === 0) return;

  // Same piecewise math mosaic-scroll uses: each cycle eases over the
  // transition then holds at the new rest position.
  const cycleIdx = Math.floor(tMs / CYCLE_MS);
  const localInCycle = ((tMs % CYCLE_MS) + CYCLE_MS) % CYCLE_MS;
  let scrollX: number;
  if (localInCycle < TRANSITION_MS) {
    const p = easeInOutCubic(localInCycle / TRANSITION_MS);
    scrollX = (cycleIdx + p) * STRIDE;
  } else {
    scrollX = (cycleIdx + 1) * STRIDE;
  }

  // At rest at scrollX = N * STRIDE, card N must sit centered on the canvas.
  // Anchor each card's CENTER (not left edge) so per-image variable widths
  // still land their midline on the slot's midpoint at rest.
  const canvasCenterX = w / 2;
  const canvasCenterY = h / 2;
  const contentW = total * STRIDE;
  // Wrap into [0, contentW) — signed-safe (JS modulo preserves sign).
  const wrapped = ((scrollX % contentW) + contentW) % contentW;

  // Side-card scale-down (Jun 13 — visible growth as a card slides toward
  // center sells the motion). Each card's distance from the canvas center
  // in stride units drives a scale factor: 1.0 at the hero position, lerps
  // down to MIN_SCALE at ±1 stride and stays flat beyond. Scale is applied
  // around the card's logical center so the position math is unchanged —
  // just the rendered w/h shrinks.
  const MIN_SCALE = 0.8;
  const SCALE_RANGE = 1 - MIN_SCALE; // 0.2

  // Per-image card dimensions — fit-within MAX bounds (Jun 15 v3 aspect-
  // aware cards). cover-fit the IMAGE's aspect into the MAX rect:
  //   - Image wider than max-card aspect → cardW = MAX_CARD_W, cardH derived
  //   - Image taller than max-card aspect → cardH = MAX_CARD_H, cardW derived
  // Falls back to (MAX_CARD_W, MAX_CARD_H) when image hasn't loaded yet
  // (naturalWidth=0) so the slot has SOMETHING to render during the first
  // frame after add.
  const cardDimsFor = (img: HTMLImageElement): { cw: number; ch: number } => {
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    if (iw <= 0 || ih <= 0) return { cw: MAX_CARD_W, ch: MAX_CARD_H };
    const imgAspect = iw / ih;
    if (imgAspect >= MAX_CARD_ASPECT) {
      // Image wider — width-bound.
      return { cw: MAX_CARD_W, ch: MAX_CARD_W / imgAspect };
    }
    // Image taller — height-bound.
    return { cw: MAX_CARD_H * imgAspect, ch: MAX_CARD_H };
  };

  // Repeat the list at -1/0/+1 strides of content so the modulo seam is
  // never visible (the off-screen repeat covers tiles that wrap around).
  for (let rep = -1; rep <= 1; rep++) {
    for (let i = 0; i < total; i++) {
      // Slot CENTER (not left edge — cards have variable widths now).
      const slotCx = canvasCenterX + i * STRIDE + rep * contentW - wrapped;
      const offsetUnits = Math.min(1, Math.abs((slotCx - canvasCenterX) / STRIDE));
      const scale = 1 - SCALE_RANGE * offsetUnits;
      const img = spec.images[i];
      const { cw, ch } = cardDimsFor(img);
      const drawW = cw * scale;
      const drawH = ch * scale;
      const drawX = slotCx - drawW / 2;
      const drawY = canvasCenterY - drawH / 2;
      if (drawX + drawW < -1 || drawX > w + 1) continue;
      drawCardChrome(ctx, img, drawX, drawY, drawW, drawH);
    }
  }
}

/** Draw a single "card" — white rounded-rect with drop shadow + the image
 *  cover-fit inside with padding. Image is clipped to a slightly-smaller
 *  inner rounded-rect so the white card frame is always visible around it. */
function drawCardChrome(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const RADIUS = Math.min(32, w / 12, h / 12);
  const PADDING = Math.round(w * 0.05);
  const innerRadius = Math.max(0, RADIUS - PADDING / 2);

  // 1. White card body with drop shadow. Save/restore around shadowBlur
  //    so the shadow doesn't bleed onto the image draw that follows.
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.14)';
  ctx.shadowBlur = 28;
  ctx.shadowOffsetY = 10;
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  if (typeof (ctx as CanvasRenderingContext2D & { roundRect?: unknown }).roundRect === 'function') {
    (ctx as CanvasRenderingContext2D & { roundRect: (x: number, y: number, w: number, h: number, r: number) => void })
      .roundRect(x, y, w, h, RADIUS);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.fill();
  ctx.restore();

  // 2. Image inside, clipped to the inner rounded-rect (so the white frame
  //    around it stays visible no matter the image aspect).
  const innerX = x + PADDING;
  const innerY = y + PADDING;
  const innerW = w - 2 * PADDING;
  const innerH = h - 2 * PADDING;

  ctx.save();
  ctx.beginPath();
  if (typeof (ctx as CanvasRenderingContext2D & { roundRect?: unknown }).roundRect === 'function') {
    (ctx as CanvasRenderingContext2D & { roundRect: (x: number, y: number, w: number, h: number, r: number) => void })
      .roundRect(innerX, innerY, innerW, innerH, innerRadius);
  } else {
    ctx.rect(innerX, innerY, innerW, innerH);
  }
  ctx.clip();

  // Cover-fit the image inside the inner rect.
  const ir = img.naturalHeight / Math.max(1, img.naturalWidth);
  const tr = innerH / innerW;
  let dW: number, dH: number, dX: number, dY: number;
  if (ir > tr) {
    dW = innerW;
    dH = innerW * ir;
    dX = innerX;
    dY = innerY + (innerH - dH) / 2;
  } else {
    dH = innerH;
    dW = innerH / ir;
    dX = innerX + (innerW - dW) / 2;
    dY = innerY;
  }
  ctx.drawImage(img, dX, dY, dW, dH);
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Marquee Rows — 3 horizontal rows stacked vertically, the user's image
// sequence sliding right-to-left in each. Same image is shown across all 3
// rows at any given moment, but each row's scroll position is phase-offset
// from the next so they don't look like one big synced image. With one
// input image the rows look like a hypnotic marquee; with 2+ they cycle
// through as the strip slides. Mirrors drawMosaicScroll's piecewise math
// transposed (horizontal scroll on fixed rows instead of vertical scroll on
// fixed columns).
// ---------------------------------------------------------------------------

function drawMarqueeRows(
  ctx: CanvasRenderingContext2D,
  spec: AnimationSpec,
  tMs: number,
  w: number,
  h: number,
): void {
  const cfg = spec.motionConfig?.kind === 'marquee-rows' ? spec.motionConfig : null;
  const ROWS = 3;                       // locked — adding row config later if needed
  const SCROLL_MS = Math.max(200, cfg?.scrollMs ?? 1500);
  const HOLD_MS = Math.max(0, cfg?.holdMs ?? 700);
  const ROW_PHASE_MS = cfg?.rowPhaseMs ?? 250;
  const TILE_W_FRAC = Math.max(0.3, Math.min(1, cfg?.tileWidthFrac ?? 1));
  const GAP = 40;                       // gap between rows (vertical) + tiles (horizontal)
  const CYCLE_MS = SCROLL_MS + HOLD_MS;

  const total = spec.images.length;
  if (total === 0) return;

  const rowH = (h - GAP * (ROWS - 1)) / ROWS;
  const tileW = w * TILE_W_FRAC;
  const STRIDE = tileW + GAP;
  const centerX = (w - tileW) / 2;       // resting x for the "current" tile

  for (let row = 0; row < ROWS; row++) {
    const rowY = row * (rowH + GAP);

    // Phase-offset local time for this row — same trick mosaic-scroll uses
    // to stagger columns. Signed-safe modulo so row 2/3 starting "ahead"
    // (negative localTime) wraps cleanly.
    const localTime = tMs - row * ROW_PHASE_MS;
    const cycleIdx = Math.floor(localTime / CYCLE_MS);
    const localInCycle = ((localTime % CYCLE_MS) + CYCLE_MS) % CYCLE_MS;
    let scrollX: number;
    if (localInCycle < SCROLL_MS) {
      const p = easeInOutCubic(localInCycle / SCROLL_MS);
      scrollX = (cycleIdx + p) * STRIDE;
    } else {
      scrollX = (cycleIdx + 1) * STRIDE;
    }

    const contentW = total * STRIDE;
    const wrapped = ((scrollX % contentW) + contentW) % contentW;

    // Draw all visible tiles in this row. Repeat factor of {-1, 0, 1}
    // covers the modulo seam.
    for (let rep = -1; rep <= 1; rep++) {
      for (let i = 0; i < total; i++) {
        const x = centerX + i * STRIDE + rep * contentW - wrapped;
        if (x + tileW < -1 || x > w + 1) continue;
        if (cfg?.splitImage) {
          drawImageStrip(ctx, spec.images[i], row, ROWS, x, rowY, tileW, rowH);
        } else {
          drawRoundedImageCover(ctx, spec.images[i], x, rowY, tileW, rowH, 0);
        }
      }
    }
  }
}

/** Render strip `stripIndex` (out of `totalStrips`) of an image into the
 *  destination rect (dx, dy, dw, dh). The image is conceptually cover-fit
 *  into a virtual rect of size (dw × dh*totalStrips) — the full-image area
 *  if all strips were stacked with no gap — and the resulting full draw is
 *  then clipped to the strip's destination rect. When all rows render their
 *  corresponding strip at the same x, the strips horizontally align and the
 *  image visually reassembles (with the canvas gap bands between rows). */
function drawImageStrip(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  stripIndex: number,
  totalStrips: number,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  const virtualH = dh * totalStrips;
  // Virtual top y so that strip `stripIndex` lands at the destination y.
  const virtualTopY = dy - stripIndex * dh;
  // Cover-fit image into (dw × virtualH).
  const imgRatio = img.naturalHeight / Math.max(1, img.naturalWidth);
  const virtRatio = virtualH / dw;
  let drawW: number, drawH: number, drawX: number, drawY: number;
  if (imgRatio > virtRatio) {
    // Image taller than virtual rect → fit width, overflow vertically.
    drawW = dw;
    drawH = dw * imgRatio;
    drawX = dx;
    drawY = virtualTopY + (virtualH - drawH) / 2;
  } else {
    // Image wider than virtual rect → fit height, overflow horizontally.
    drawH = virtualH;
    drawW = virtualH / imgRatio;
    drawX = dx + (dw - drawW) / 2;
    drawY = virtualTopY;
  }
  // Clip to the destination strip — everything outside this strip's rect
  // (other rows' content, virtual margins) gets clipped out.
  ctx.save();
  ctx.beginPath();
  ctx.rect(dx, dy, dw, dh);
  ctx.clip();
  ctx.drawImage(img, drawX, drawY, drawW, drawH);
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Stop-Motion Grid — N×M grid of tiles, each flipping in/out + cycling the
// user's images independently at its own deterministic tick rate. Discrete
// state changes (no easing) give the stop-motion feel. Tiles that roll
// "hide" at a tick leave the bg showing through, so the grid breathes.
//
// Each tile's behavior is a pure function of (cellIdx, tMs) — no per-frame
// state needed. A tiny FNV-1a hash seeds the per-cell phase, period, and
// per-tick show/image choices. Reproducible across renders (same time +
// same image set = same frame), so the live preview and the encoded MP4
// match byte-for-byte.
// ---------------------------------------------------------------------------

/** Tiny FNV-1a 32-bit hash for per-cell stop-motion seeds. */
function smgHash(...args: number[]): number {
  let h = 2166136261;
  for (const a of args) {
    h = (h ^ (a | 0)) >>> 0;
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function drawStopMotionGrid(
  ctx: CanvasRenderingContext2D,
  spec: AnimationSpec,
  tMs: number,
  w: number,
  h: number,
): void {
  const cfg = spec.motionConfig?.kind === 'stop-motion-grid' ? spec.motionConfig : null;
  const COLS = Math.max(1, cfg?.cols ?? 3);
  const ROWS = Math.max(1, cfg?.rows ?? 2);
  const TICK_MIN = Math.max(100, cfg?.tickMinMs ?? 600);
  const TICK_MAX = Math.max(TICK_MIN + 100, cfg?.tickMaxMs ?? 1800);
  const SHOW_PROB = Math.max(0, Math.min(1, cfg?.showProbability ?? 0.8));
  const TILE_RATIO = cfg?.tileAspectRatio ?? 1;
  const GAP = 40;
  const SIDE_PADDING = 40;              // canvas-edge breathing room (Jun 13)
  const RADIUS = Math.round(w * 0.012);

  const total = spec.images.length;
  if (total === 0) return;

  // Fit a COLS×ROWS grid of tiles (aspect TILE_RATIO = h/w) into the canvas
  // with GAP between tiles + a 5% vertical safety margin + 40px L/R padding.
  // Try width-fit first; if the resulting height overflows, fall back to
  // height-fit. Center the grid.
  const widthFitTileW = (w - 2 * SIDE_PADDING - GAP * (COLS - 1)) / COLS;
  const widthFitTileH = widthFitTileW * TILE_RATIO;
  const widthFitGridH = widthFitTileH * ROWS + GAP * (ROWS - 1);
  let tileW: number, tileH: number;
  if (widthFitGridH <= h * 0.95) {
    tileW = widthFitTileW;
    tileH = widthFitTileH;
  } else {
    tileH = (h * 0.95 - GAP * (ROWS - 1)) / ROWS;
    tileW = tileH / TILE_RATIO;
  }
  const gridW = tileW * COLS + GAP * (COLS - 1);
  const gridH = tileH * ROWS + GAP * (ROWS - 1);
  const gridX0 = (w - gridW) / 2;
  const gridY0 = (h - gridH) / 2;

  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const cellIdx = r * COLS + c;
      const x = gridX0 + c * (tileW + GAP);
      const y = gridY0 + r * (tileH + GAP);

      // Per-cell deterministic phase + tick period. Range [TICK_MIN, TICK_MAX)
      // so some tiles change every ~600ms, others closer to ~1800ms. Phase
      // staggers when their tick boundaries land so cells don't all flip
      // simultaneously.
      const phaseMs = smgHash(cellIdx, 17) % 1000;
      const periodMs = TICK_MIN + (smgHash(cellIdx, 31) % (TICK_MAX - TICK_MIN));
      const tickIdx = Math.floor((tMs + phaseMs) / periodMs);

      // Show/hide roll for this (cell, tick). 0..999 against SHOW_PROB*1000.
      const showRoll = smgHash(cellIdx, tickIdx, 53) % 1000;
      if (showRoll >= SHOW_PROB * 1000) continue; // empty cell — bg shows through

      // Which image at this tick? Hash drives a deterministic pick from the
      // user's input set — same (cell, tick) always picks the same image, so
      // the live preview and encoded MP4 stay byte-identical.
      const imgIdx = smgHash(cellIdx, tickIdx, 89) % total;
      drawRoundedImageCover(ctx, spec.images[imgIdx], x, y, tileW, tileH, RADIUS);
    }
  }
}

// ---------------------------------------------------------------------------
// Angled Cut — image split by a single angled line into two polygonal regions.
// Orientation controls the cut axis:
//   - 'horizontal' (default): near-horizontal line, top + bottom regions
//     slide in from off-canvas top + bottom respectively
//   - 'vertical': near-vertical line, left + right regions slide in from
//     off-canvas left + right respectively
// Either way, at rest both regions sit at offset 0 — the two halves of the
// SAME image align contiguously, the seam is invisible (no padding between
// splits). Phase offset between regions gives the sliced cascade reveal.
// ---------------------------------------------------------------------------

function drawAngledCut(
  ctx: CanvasRenderingContext2D,
  spec: AnimationSpec,
  tMs: number,
  w: number,
  h: number,
): void {
  const cfg = spec.motionConfig?.kind === 'angled-cut' ? spec.motionConfig : null;
  const ORIENTATION = cfg?.orientation ?? 'horizontal';
  const isHorizontal = ORIENTATION === 'horizontal';
  const TILT_DEG = cfg?.tiltDeg ?? 12;
  const SCROLL_MS = Math.max(200, cfg?.scrollMs ?? 1100);
  const HOLD_MS = Math.max(0, cfg?.holdMs ?? 1500);
  const PHASE_MS = cfg?.regionPhaseMs ?? 350;
  const CYCLE_MS = SCROLL_MS + HOLD_MS;
  // Image slide axis is ALWAYS horizontal (right-to-left) regardless of the
  // cut orientation. The cut just defines which polygonal regions show
  // which content; both regions translate their image along the x-axis
  // with phase offset. STRIDE = canvas width per cycle.
  const STRIDE = w;

  const total = spec.images.length;
  if (total === 0) return;

  // Compute the angled line's endpoints. The line passes through canvas
  // center, tilted from the orientation's base axis.
  const tiltRad = (TILT_DEG * Math.PI) / 180;
  let region0Path: number[][];          // first region (top / left)
  let region1Path: number[][];          // second region (bottom / right)
  if (isHorizontal) {
    // Near-horizontal line. Endpoints on canvas left + right edges.
    // tiltDeg from horizontal: positive tilt = LINE RISES TO THE RIGHT
    // (right endpoint higher up, smaller y; left endpoint lower down,
    // larger y). Reads as a "/" slope visually.
    const yOffset = (w / 2) * Math.tan(tiltRad);
    const yLeft = h / 2 + yOffset;      // lower (larger y)
    const yRight = h / 2 - yOffset;     // higher (smaller y)
    // Top region: above the line.
    region0Path = [[0, 0], [w, 0], [w, yRight], [0, yLeft]];
    // Bottom region: below the line.
    region1Path = [[0, yLeft], [w, yRight], [w, h], [0, h]];
  } else {
    // Near-vertical line. Endpoints on canvas top + bottom edges.
    const xOffset = (h / 2) * Math.tan(tiltRad);
    const xTop = w / 2 - xOffset;
    const xBot = w / 2 + xOffset;
    // Left region.
    region0Path = [[0, 0], [xTop, 0], [xBot, h], [0, h]];
    // Right region.
    region1Path = [[xTop, 0], [w, 0], [w, h], [xBot, h]];
  }

  const scrollFor = (regionIdx: number): number => {
    const localTime = tMs - regionIdx * PHASE_MS;
    const cycleIdx = Math.floor(localTime / CYCLE_MS);
    const localInCycle = ((localTime % CYCLE_MS) + CYCLE_MS) % CYCLE_MS;
    if (localInCycle < SCROLL_MS) {
      const p = easeInOutCubic(localInCycle / SCROLL_MS);
      return (cycleIdx + p) * STRIDE;
    }
    return (cycleIdx + 1) * STRIDE;
  };

  const drawRegion = (regionIdx: 0 | 1, scroll: number) => {
    // Clip to the region polygon.
    ctx.save();
    ctx.beginPath();
    const path = regionIdx === 0 ? region0Path : region1Path;
    ctx.moveTo(path[0][0], path[0][1]);
    for (let i = 1; i < path.length; i++) {
      ctx.lineTo(path[i][0], path[i][1]);
    }
    ctx.closePath();
    ctx.clip();

    // Slide-band clip — anchored at each image's offsetX, sized exactly to
    // canvas width × height. Without this, cover-fit images whose native
    // aspect doesn't match the canvas's (e.g. a 1:1 source on a 4:5 canvas)
    // produce a draw rect wider than canvas width (the image overflows
    // horizontally to fill the canvas height). When the next image gets
    // drawn at STRIDE - subOffset, its overflow immediately covers ~135px
    // of canvas the moment the guard kicks in, looking like an abrupt jerk.
    // Per-image clip to a STRIDE-wide band keeps adjacent draws contiguous
    // and overlap-free regardless of source aspect → smooth slide.
    const drawSlideBand = (img: HTMLImageElement, offX: number) => {
      ctx.save();
      ctx.beginPath();
      ctx.rect(offX, 0, w, h);
      ctx.clip();
      drawCoverFitAt(ctx, img, offX, 0, w, h);
      ctx.restore();
    };

    // Single-image input (Jun 15 v3 fix — was: skipped the next-image draw
    // entirely, leaving the right side of the canvas BLACK during the slide
    // phase). With 1 image there's no meaningful "transition" — both regions
    // would just slide off to reveal the same image again, with a visible
    // seam where the two cover-fit copies meet. Cleanest: skip the slide
    // altogether and draw the image fixed at offset 0 so the region is
    // always fully covered. Both regions show the same static image, the
    // angled cut is invisible (both halves identical), but the broken
    // black-area bug is resolved. Angled-cut is recommended for 2-3 images
    // — single-image is an edge case and a static display is acceptable.
    if (total === 1) {
      drawSlideBand(spec.images[0], 0);
      ctx.restore();
      return;
    }

    // At scroll = 0, image 0 is positioned at offset 0 → assembled within
    // this region. During the slide, the current image translates along
    // the perpendicular axis (negative = leaving) and the next image
    // enters from the other side.
    const wrapped = ((scroll % (total * STRIDE)) + total * STRIDE) % (total * STRIDE);
    const baseIdx = Math.floor(wrapped / STRIDE);
    const subOffset = wrapped - baseIdx * STRIDE;

    // Current image leaving rightward: translates leftward (-subOffset on x).
    drawSlideBand(spec.images[baseIdx % total], -subOffset);
    // Next image entering from the right edge.
    if (subOffset > 0.5) {
      drawSlideBand(spec.images[(baseIdx + 1) % total], STRIDE - subOffset);
    }
    ctx.restore();
  };

  drawRegion(0, scrollFor(0));
  drawRegion(1, scrollFor(1));
}

/** Cover-fit an image at (offsetX, offsetY) with canvas-sized destination
 *  (w, h) — used by motions that translate the image and clip to a custom
 *  shape externally (the outer clip restricts what's drawn). Skips the inner
 *  rect clip that drawRoundedImageCover would do. */
function drawCoverFitAt(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  offsetX: number,
  offsetY: number,
  w: number,
  h: number,
): void {
  const ir = img.naturalHeight / Math.max(1, img.naturalWidth);
  const tr = h / w;
  let dW: number, dH: number, dX: number, dY: number;
  if (ir > tr) {
    dW = w;
    dH = w * ir;
    dX = 0;
    dY = (h - dH) / 2;
  } else {
    dH = h;
    dW = h / ir;
    dX = (w - dW) / 2;
    dY = 0;
  }
  ctx.drawImage(img, dX + offsetX, dY + offsetY, dW, dH);
}

// ---------------------------------------------------------------------------
// Collage Stack — images stop-motion onto a stack at discrete tick intervals.
// Each card lands with a slight rotation + position jitter (deterministic via
// FNV hash) and a thin white photo-paper border with drop shadow. After the
// last image lands, holds for a beat then the cycle restarts with a clean
// canvas → first card → next... matches the scrapbook / paper-collage feel.
// ---------------------------------------------------------------------------

function drawCollageStack(
  ctx: CanvasRenderingContext2D,
  spec: AnimationSpec,
  tMs: number,
  w: number,
  h: number,
): void {
  const cfg = spec.motionConfig?.kind === 'collage-stack' ? spec.motionConfig : null;
  const TICK_MS = Math.max(150, cfg?.tickMs ?? 700);
  const HOLD_MS = Math.max(0, cfg?.holdMs ?? 1800);
  const MAX_ROT_DEG = cfg?.maxRotationDeg ?? 8;
  const CARD_W_FRAC = Math.max(0.2, Math.min(1, cfg?.cardWidthFrac ?? 0.65));
  const W_JITTER = Math.max(0, Math.min(0.3, cfg?.cardWidthJitter ?? 0.1));

  const total = spec.images.length;
  if (total === 0) return;

  // One full cycle = each image landing (total * TICK_MS) + hold + a brief
  // reset window so the first frame of the next cycle shows a clean canvas.
  const cycleMs = total * TICK_MS + HOLD_MS;
  const tInCycle = ((tMs % cycleMs) + cycleMs) % cycleMs;

  // visibleCount = how many cards are currently on the stack at this moment.
  // First card lands at t=0 (visible immediately) → visibleCount = 1.
  // Each subsequent TICK_MS adds one until we hit `total`, then we hold.
  const visibleCount = Math.min(total, Math.floor(tInCycle / TICK_MS) + 1);

  for (let i = 0; i < visibleCount; i++) {
    const img = spec.images[i];
    // Deterministic per-card placement from FNV hash. Same image at same
    // index always lands at the same spot, so the live preview + encoded
    // MP4 stay byte-identical.
    const seedR = smgHash(i, 11);
    const seedW = smgHash(i, 23);
    const seedX = smgHash(i, 37);
    const seedY = smgHash(i, 41);

    const rotDeg = (((seedR % 1000) / 1000) * 2 - 1) * MAX_ROT_DEG;
    const widthJitter = (((seedW % 1000) / 1000) * 2 - 1) * W_JITTER;
    const cardW = w * (CARD_W_FRAC + widthJitter);
    const imgRatio = img.naturalHeight / Math.max(1, img.naturalWidth);
    const cardH = cardW * imgRatio;
    // Position jitter — small offsets from canvas center.
    const cx = w / 2 + w * (((seedX % 1000) / 1000) * 2 - 1) * 0.08;
    const cy = h / 2 + h * (((seedY % 1000) / 1000) * 2 - 1) * 0.08;

    const BORDER = Math.round(w * 0.012);         // ~13px paper border @ 1080w
    const cardX = -cardW / 2;
    const cardY = -cardH / 2;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((rotDeg * Math.PI) / 180);
    // Drop shadow gets drawn under the white paper rect; clear it before
    // the image draw so the image edges stay sharp.
    ctx.shadowColor = 'rgba(0, 0, 0, 0.18)';
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 10;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(cardX - BORDER, cardY - BORDER, cardW + 2 * BORDER, cardH + 2 * BORDER);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.drawImage(img, cardX, cardY, cardW, cardH);
    ctx.restore();
  }
}

// ---------------------------------------------------------------------------
// Text layout (simple word-wrap, centered block)
// ---------------------------------------------------------------------------

interface LaidOutWord {
  text: string;
  x: number; // left edge
  y: number; // baseline
  index: number; // global word index (drives stagger)
}

interface TextLayout {
  words: LaidOutWord[];
  lines: { text: string; x: number; y: number }[];
  fontPx: number;
  lineH: number;
  blockTop: number;
  blockBottom: number;
}

function layoutText(
  ctx: CanvasRenderingContext2D,
  text: string,
  w: number,
  fontPx: number,
  weight: number,
  centerY: number,
  family = 'Manrope',
  align: 'left' | 'center' | 'right' = 'center',
): TextLayout {
  ctx.font = `${weight} ${fontPx}px ${family}, sans-serif`;
  const maxW = w * 0.84;
  const tokens = text.split(/\s+/).filter(Boolean);
  const lineH = fontPx * 1.18;

  // Greedy wrap.
  const lineTokens: string[][] = [];
  let cur: string[] = [];
  for (const tok of tokens) {
    const candidate = [...cur, tok].join(' ');
    if (cur.length > 0 && ctx.measureText(candidate).width > maxW) {
      lineTokens.push(cur);
      cur = [tok];
    } else {
      cur.push(tok);
    }
  }
  if (cur.length > 0) lineTokens.push(cur);

  const blockH = lineTokens.length * lineH;
  const top = centerY - blockH / 2;

  const words: LaidOutWord[] = [];
  const lines: { text: string; x: number; y: number }[] = [];
  const spaceW = ctx.measureText(' ').width;
  let wordIndex = 0;
  lineTokens.forEach((lt, li) => {
    const lineText = lt.join(' ');
    const lineW = ctx.measureText(lineText).width;
    const baseline = top + li * lineH + fontPx; // approx baseline
    let x = align === 'left'
      ? w * 0.08
      : align === 'right'
        ? w * 0.92 - lineW
        : (w - lineW) / 2;
    lines.push({ text: lineText, x, y: baseline });
    for (const tok of lt) {
      words.push({ text: tok, x, y: baseline, index: wordIndex });
      x += ctx.measureText(tok).width + spaceW;
      wordIndex += 1;
    }
  });

  return {
    words,
    lines,
    fontPx,
    lineH,
    blockTop: top,
    blockBottom: top + blockH,
  };
}

// ---------------------------------------------------------------------------
// Animated text engine — shared by the title overlay and slide captions.
// Parametrized geometry (centerY, font size) + timing (local t, entrance
// budget) + a global `fade` multiplier (used for caption exit fades —
// every alpha in here multiplies by it).
// ---------------------------------------------------------------------------

interface AnimatedTextOpts {
  style: TextStyle;
  headline: string;
  subline?: string;
  textColor: string;
  accentColor: string;
  /** Optional background fill — renders as per-line rounded "highlighter"
   *  pills hugging each line of the headline. Null/undefined = no fill.
   *  Static size (computed from final text layout); fades with the text's
   *  exit-fade but doesn't animate on entrance (text animates on top). */
  bgColor?: string | null;
  /** Self-hosted family + a weight that's actually loaded (ANIM_FONTS). */
  fontFamily: string;
  fontWeight: number;
  align: 'left' | 'center' | 'right';
  /** Local time since this text's window started. */
  t: number;
  /** Entrance budget — all entrances complete within this window. */
  animWindow: number;
  /** 0..1 visibility multiplier (caption exit fade). */
  fade: number;
  centerY: number;
  headlinePx: number;
  sublinePx: number;
}

function drawAnimatedText(
  ctx: CanvasRenderingContext2D,
  w: number,
  opts: AnimatedTextOpts,
): void {
  const {
    style, headline, subline, textColor, accentColor,
    bgColor,
    fontFamily, fontWeight, align,
    t, animWindow, fade, centerY, headlinePx, sublinePx,
  } = opts;
  const font = (px: number) => `${fontWeight} ${px}px ${fontFamily}, sans-serif`;
  if (fade <= 0) return;
  const hasSub = !!subline?.trim();

  ctx.textBaseline = 'alphabetic';

  // Helper — draw a single rounded "highlighter pill" hugging the text
  // at (x, baselineY) with the given pill width. Pills size to the
  // text's font metrics: ~32% horizontal padding, ~18% vertical, corner
  // radius ~22% of fontPx (capped at 14px). The caller supplies any
  // entrance/exit alpha so each style's animation chains in cleanly.
  const drawTextPill = (
    px: number, baselineY: number, pillW: number, alpha: number,
    dy = 0,
  ): void => {
    if (!bgColor || alpha <= 0.001 || pillW <= 0) return;
    const padX = Math.round(headlinePx * 0.32);
    const padY = Math.round(headlinePx * 0.18);
    const radius = Math.min(Math.round(headlinePx * 0.22), 14);
    const rectX = px - padX;
    // baselineY is glyph baseline. Top of glyph ≈ y - fontPx; pad above
    // and below for breathing room.
    const rectY = baselineY + dy - headlinePx + padY * 0.4;
    const rectW = pillW + padX * 2;
    const rectH = headlinePx + padY * 1.2;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    const hasRoundRect = typeof (ctx as CanvasRenderingContext2D & { roundRect?: unknown }).roundRect === 'function';
    if (hasRoundRect) {
      (ctx as CanvasRenderingContext2D & { roundRect: (x: number, y: number, w: number, h: number, r: number) => void })
        .roundRect(rectX, rectY, rectW, rectH, radius);
    } else {
      ctx.rect(rectX, rectY, rectW, rectH);
    }
    ctx.fill();
    ctx.restore();
  };

  if (style === 'word-pop') {
    const layout = layoutText(ctx, headline, w, headlinePx, fontWeight, centerY, fontFamily, align);
    const nWords = layout.words.length;
    const stagger = nWords > 1 ? (animWindow * 0.7) / nWords : 0;
    const wordDur = Math.min(420, animWindow * 0.3);
    // Bg pill (Jun 15) — per-line, fades in alongside the first word
    // of each line. Each word index's start time is `100 + i * stagger`;
    // pill fades in over the entrance budget so it lands as the first
    // word starts popping. ctx.font set so measureText returns line widths.
    if (bgColor) {
      ctx.font = font(layout.fontPx);
      // Map line index → first word's index in `layout.words`.
      let cursor = 0;
      for (let li = 0; li < layout.lines.length; li++) {
        const line = layout.lines[li];
        const lineW = ctx.measureText(line.text).width;
        const wordsInLine = line.text.split(/\s+/).filter(Boolean).length;
        const firstWordIdx = cursor;
        cursor += wordsInLine;
        const start = 100 + firstWordIdx * stagger;
        const p = clamp01((t - start) / Math.max(wordDur, 200));
        if (p <= 0) continue;
        drawTextPill(line.x, line.y, lineW, easeOutCubic(p) * fade);
      }
    }
    for (const word of layout.words) {
      const start = 100 + word.index * stagger;
      const p = clamp01((t - start) / wordDur);
      if (p <= 0) continue;
      const scale = 0.6 + 0.4 * easeOutBack(p);
      ctx.save();
      ctx.globalAlpha = easeOutCubic(p) * fade;
      ctx.font = font(layout.fontPx);
      const ww = ctx.measureText(word.text).width;
      const cx = word.x + ww / 2;
      const cy = word.y - layout.fontPx * 0.35;
      ctx.translate(cx, cy);
      ctx.scale(scale, scale);
      ctx.fillStyle = textColor;
      ctx.fillText(word.text, -ww / 2, layout.fontPx * 0.35);
      ctx.restore();
    }
    if (hasSub) {
      const subStart = 100 + nWords * stagger + 100;
      const p = clamp01((t - subStart) / 400);
      if (p > 0) {
        ctx.globalAlpha = easeOutCubic(p) * fade;
        ctx.font = `${Math.min(600, fontWeight)} ${sublinePx}px ${fontFamily}, sans-serif`;
        ctx.fillStyle = accentColor;
        const sw = ctx.measureText(subline!).width;
        ctx.fillText(subline!, (w - sw) / 2, layout.blockBottom + sublinePx * 1.8);
        ctx.globalAlpha = 1;
      }
    }
  } else if (style === 'slide-in') {
    const layout = layoutText(ctx, headline, w, headlinePx, fontWeight, centerY, fontFamily, align);
    const lineDur = Math.min(550, animWindow * 0.6);
    const stagger = layout.lines.length > 1 ? (animWindow * 0.5) / layout.lines.length : 0;
    // Pre-measure all line widths (Jun 15 — needed for the pill slide).
    ctx.font = font(headlinePx);
    const lineWidths = layout.lines.map((l) => ctx.measureText(l.text).width);
    layout.lines.forEach((line, li) => {
      const start = 100 + li * stagger;
      const p = clamp01((t - start) / lineDur);
      if (p <= 0) return;
      const dy = (1 - easeOutCubic(p)) * headlinePx * 1.2;
      const lineAlpha = easeOutCubic(p) * fade;
      // Bg pill slides up WITH the line — same dy, same alpha → text sits
      // on the pill perfectly throughout the entrance, no decoupling.
      drawTextPill(line.x, line.y, lineWidths[li], lineAlpha, dy);
      ctx.globalAlpha = lineAlpha;
      ctx.font = font(headlinePx);
      ctx.fillStyle = textColor;
      ctx.fillText(line.text, line.x, line.y + dy);
      ctx.globalAlpha = 1;
    });
    if (hasSub) {
      const start = 100 + layout.lines.length * stagger + 150;
      const p = clamp01((t - start) / lineDur);
      if (p > 0) {
        const dy = (1 - easeOutCubic(p)) * sublinePx * 1.5;
        ctx.globalAlpha = easeOutCubic(p) * fade;
        ctx.font = `${Math.min(600, fontWeight)} ${sublinePx}px ${fontFamily}, sans-serif`;
        ctx.fillStyle = accentColor;
        const sw = ctx.measureText(subline!).width;
        ctx.fillText(subline!, (w - sw) / 2, layout.blockBottom + sublinePx * 1.8 + dy);
        ctx.globalAlpha = 1;
      }
    }
  } else {
    // typewriter — characters reveal over the anim window; block caret
    // blinks at 500ms cadence, solid while typing.
    const chars = headline.length;
    const typeDur = animWindow * 0.8;
    const visible = Math.min(chars, Math.floor((t / typeDur) * chars));
    const shown = headline.slice(0, visible);
    const layout = layoutText(ctx, headline, w, headlinePx, fontWeight, centerY, fontFamily, align);
    ctx.font = font(headlinePx);
    let budget = shown.length;
    let caretX = 0;
    let caretY = layout.lines[0]?.y ?? centerY;
    // Pass 1 — bg pills (Jun 15) sized to each line's REVEALED text so
    // far. The pill grows horizontally as characters appear → reads as
    // "highlighter sweeping with the typing". A line that hasn't started
    // typing yet (budget exhausted) is skipped.
    if (bgColor) {
      let pillBudget = shown.length;
      for (const line of layout.lines) {
        if (pillBudget <= 0) break;
        const take = line.text.slice(0, pillBudget);
        if (take.length > 0) {
          const w0 = ctx.measureText(take).width;
          drawTextPill(line.x, line.y, w0, fade);
        }
        pillBudget -= line.text.length + 1;
      }
    }
    // Pass 2 — text + caret.
    ctx.globalAlpha = fade;
    ctx.fillStyle = textColor;
    for (const line of layout.lines) {
      if (budget <= 0) break;
      const take = line.text.slice(0, budget);
      ctx.fillText(take, line.x, line.y);
      caretX = line.x + ctx.measureText(take).width;
      caretY = line.y;
      budget -= line.text.length + 1; // +1 for the joining space
    }
    const typing = visible < chars;
    const blinkOn = typing || Math.floor(t / 500) % 2 === 0;
    if (blinkOn) {
      ctx.fillStyle = accentColor;
      ctx.fillRect(caretX + 6, caretY - headlinePx * 0.82, headlinePx * 0.1, headlinePx);
    }
    if (hasSub) {
      const p = clamp01((t - typeDur - 200) / 400);
      if (p > 0) {
        ctx.globalAlpha = easeOutCubic(p) * fade;
        ctx.font = `${Math.min(600, fontWeight)} ${sublinePx}px ${fontFamily}, sans-serif`;
        ctx.fillStyle = textColor;
        const sw = ctx.measureText(subline!).width;
        ctx.fillText(subline!, (w - sw) / 2, layout.blockBottom + sublinePx * 1.8);
      }
    }
    ctx.globalAlpha = 1;
  }
  ctx.globalAlpha = 1;
}

// ---------------------------------------------------------------------------
// Title overlay — spans the whole video. Center-weighted radial scrim.
// ---------------------------------------------------------------------------

function drawTitle(
  ctx: CanvasRenderingContext2D,
  spec: AnimationSpec,
  tMs: number,
  w: number,
  h: number,
): void {
  const text = spec.text;
  if (!text || !text.headline.trim()) return;
  const totalMs = specDurationMs(spec);
  const t = ((tMs % totalMs) + totalMs) % totalMs;

  // Legibility scrim — center-weighted radial dim, lighter than a full
  // flat overlay so the motion layer stays visible at the edges.
  const grad = ctx.createRadialGradient(
    w / 2, h / 2, Math.min(w, h) * 0.15,
    w / 2, h / 2, Math.max(w, h) * 0.75,
  );
  grad.addColorStop(0, 'rgba(0,0,0,0.42)');
  grad.addColorStop(1, 'rgba(0,0,0,0.18)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const headlinePx = Math.round(w * (text.headline.length > 40 ? 0.07 : 0.085));
  const hasSub = !!text.subline?.trim();

  drawAnimatedText(ctx, w, {
    style: text.style,
    headline: text.headline,
    subline: text.subline,
    textColor: text.textColor,
    accentColor: text.accentColor,
    fontFamily: 'Manrope',
    fontWeight: 800,
    align: 'center',
    t,
    animWindow: totalMs * 0.6,
    fade: 1,
    centerY: hasSub ? h * 0.46 : h * 0.5,
    headlinePx,
    sublinePx: Math.round(w * 0.038),
  });
}

// ---------------------------------------------------------------------------
// Global cycling captions (Jun 15 v3 — was per-slide / per-image).
// Each caption is independent of which image is currently showing. The
// total animation duration is divided EVENLY across the caption list;
// captions appear in order and cycle. With N captions, each gets
// totalMs/N time on screen. The renderer's entrance animation + exit
// fade behave per-caption-window instead of per-image-slide window.
// Lower-third placement + soft gradient scrim around the caption block
// so it never fights with the image content.
// ---------------------------------------------------------------------------

function drawCaptions(
  ctx: CanvasRenderingContext2D,
  spec: AnimationSpec,
  tMs: number,
  w: number,
  h: number,
): void {
  const captions = spec.captions;
  if (!captions || captions.length === 0 || spec.images.length === 0) return;

  // Per-caption window: totalMs / N captions. Index + local time within
  // window derived from global tMs (NOT from the current image slide).
  const totalMs = specDurationMs(spec);
  if (totalMs <= 0) return;
  const captionWindow = totalMs / captions.length;
  const wrappedT = ((tMs % totalMs) + totalMs) % totalMs;
  const capIdx = Math.min(captions.length - 1, Math.floor(wrappedT / captionWindow));
  const local = wrappedT - capIdx * captionWindow;
  const per = captionWindow;
  const caption = captions[capIdx]?.trim();
  if (!caption) return;

  // Exit fade over the caption window's last ~300ms (clamped for fast cuts).
  const exitWindow = Math.min(300, per * 0.3);
  const exitFade = local > per - exitWindow
    ? 1 - (local - (per - exitWindow)) / exitWindow
    : 1;
  const capY = spec.captionY ?? 0.8;
  // Legibility scrim (Jul 24 — now selectable, was always a centered band).
  // Eases in over the first 200ms so hard-cut styles don't pop a dark band
  // instantly. Four variants:
  //   'none'   → skip entirely
  //   'band'   → soft dark band centered on the caption block (follows placement)
  //   'bottom' → gradient rising from the bottom edge (classic lower-third)
  //   'top'    → gradient falling from the top edge
  const scrimStyle = spec.captionScrim ?? 'band';
  const scrimIn = clamp01(local / 200);
  const scrimA = scrimIn * exitFade;

  if (scrimStyle !== 'none' && scrimA > 0.01) {
    if (scrimStyle === 'band') {
      const a = 0.5 * scrimA;
      const y0 = Math.max(0, capY - 0.24) * h;
      const y1 = Math.min(1, capY + 0.24) * h;
      const grad = ctx.createLinearGradient(0, y0, 0, y1);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(0.5, `rgba(0,0,0,${a.toFixed(3)})`);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, y0, w, y1 - y0);
    } else if (scrimStyle === 'bottom') {
      // Full-width gradient anchored at the bottom edge: opaque at the
      // very bottom → transparent by ~55% up the frame.
      const a = 0.72 * scrimA;
      const y0 = h * 0.45;
      const grad = ctx.createLinearGradient(0, y0, 0, h);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, `rgba(0,0,0,${a.toFixed(3)})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, y0, w, h - y0);
    } else if (scrimStyle === 'top') {
      // Mirror of 'bottom' — opaque at the top edge → transparent by ~55% down.
      const a = 0.72 * scrimA;
      const y1 = h * 0.55;
      const grad = ctx.createLinearGradient(0, 0, 0, y1);
      grad.addColorStop(0, `rgba(0,0,0,${a.toFixed(3)})`);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, y1);
    }
  }

  const scale = spec.captionScale ?? 1;
  drawAnimatedText(ctx, w, {
    style: spec.captionStyle ?? 'slide-in',
    headline: caption,
    textColor: spec.captionTextColor ?? '#FFFFFF',
    accentColor: '#FFD166',
    bgColor: spec.captionBgColor ?? null,
    fontFamily: spec.captionFont ?? 'Manrope',
    fontWeight: spec.captionWeight ?? 800,
    align: spec.captionAlign ?? 'center',
    t: local,
    // Entrance budget scales with the slide but stays snappy — at
    // 0.5s/image the caption still fully lands before the cut.
    animWindow: Math.min(900, per * 0.5),
    fade: exitFade,
    centerY: h * capY,
    headlinePx: Math.round(w * (caption.length > 30 ? 0.045 : 0.055) * scale),
    sublinePx: Math.round(w * 0.032 * scale),
  });
}

// ---------------------------------------------------------------------------
// Entry point — motion layer, then captions, then the title on top.
// ---------------------------------------------------------------------------

export function drawAnimationFrame(
  ctx: CanvasRenderingContext2D,
  spec: AnimationSpec,
  tMs: number,
  w: number,
  h: number,
): void {
  drawMotion(ctx, spec, tMs, w, h);
  drawCaptions(ctx, spec, tMs, w, h);
  drawTitle(ctx, spec, tMs, w, h);
}
